/**
 * The surface the security views are currently scoped to.
 *
 * The Atlas pages are mounted twice — unscoped under `/security-atlas/*`, and
 * pinned to one surface under a product module such as `/gemini/agents/security/*`.
 * Rather than thread a `surface` prop through every page and every call site,
 * the scope is set once by the route wrapper and read by each API client's
 * query-string builder.
 *
 * Deliberately module-level rather than React context: the clients are plain
 * modules with no access to the React tree, and the routes are mutually
 * exclusive, so exactly one scope is ever active. `<SurfaceScope>` sets it on
 * mount and clears it on unmount, so navigating from a product module back to
 * Atlas widens again — a scope that leaked would silently show one surface's
 * data under a heading claiming to show everything.
 *
 * This is a display convenience, NOT an access control. The server derives the
 * caller's permitted surfaces from their token and applies them regardless of
 * what arrives here; a scope sent from the browser may narrow the result, never
 * widen it.
 */

let current = null;
let currentAtlas = null;
let emptyOnUnavailable = false;

/**
 * `surface` is the security plane's name for the pinned surface (`otel`, `gemini_enterprise`,
 * `copilot_studio`): what the security service, the graph and the metrics engine read.
 * `options.atlasSurface` is the same surface's Agent Atlas id (`custom`, `gemini-enterprise`,
 * `copilot-studio`): what Atlas's own reads (`/users`, `/adoption`, `/policy`) match exactly. A
 * mount that names none sends `surface` to Atlas too (the behaviour before the two were split).
 */
export function setSurfaceScope(surface, options = {}) {
  current = surface || null;
  currentAtlas = (current && options.atlasSurface) || null;
  emptyOnUnavailable = options.emptyOnUnavailable === true;
}

export function clearSurfaceScope() {
  current = null;
  currentAtlas = null;
  emptyOnUnavailable = false;
}

export function getSurfaceScope() {
  return current;
}

/** The pinned surface as Agent Atlas names it (its id), or the security name when none was given. */
export function getAtlasSurfaceScope() {
  return currentAtlas || current;
}

/**
 * Whether a failed read should be represented by the page's real empty layout.
 * This is enabled only for newly namespaced product pages whose read APIs are
 * being deployed incrementally. It never changes mutation behaviour.
 */
export function shouldUseEmptyResponses() {
  return emptyOnUnavailable;
}

/** Route check is a safety net for hot reloads and layout-effect ordering. */
export function isCustomAgentsRoute() {
  if (typeof window === 'undefined' || !window.location) return false;
  return /^\/custom-agents(?:\/|$)/.test(window.location.pathname || '');
}

const statusFrom = (error) => {
  const direct = Number(error?.status ?? error?.response?.status);
  if (Number.isFinite(direct) && direct > 0) return direct;
  const match = String(error?.message || '').match(/(?:status(?: code)?[: ]+|api\s+)(\d{3})\b/i);
  return match ? Number(match[1]) : null;
};

/**
 * Only an endpoint that is not deployed in this environment yet (404/410) may
 * render the page's empty layout. Everything else (5xx, timeouts, rate limits,
 * network failures, auth) is a failed read and must reach the page's error
 * state: a broken mount must never read "0 incidents" (audit row C1).
 */
export function isUnavailableReadError(error) {
  const message = String(error?.message || '');
  if (/unauthori[sz]ed|forbidden|permission denied|access denied|session expired|sign in/i.test(message)) {
    return false;
  }
  const status = statusFrom(error);
  return status === 404 || status === 410;
}

/**
 * Run a read and, only inside an opted-in Custom surface, substitute a typed
 * empty response when its endpoint is unavailable. Capture the setting before
 * awaiting so navigation during a slow request cannot change its semantics.
 */
export async function readOrEmpty(read, fallback) {
  const onCustomAgentsRoute = isCustomAgentsRoute();
  const mayUseEmpty = emptyOnUnavailable || onCustomAgentsRoute;
  const emptyValue = () => (typeof fallback === 'function' ? fallback() : fallback);
  try {
    const value = await read();
    if (!mayUseEmpty) return value;
    if (value == null) return emptyValue();
    const empty = emptyValue();
    if (
      empty && value
      && typeof empty === 'object' && !Array.isArray(empty)
      && typeof value === 'object' && !Array.isArray(value)
    ) {
      return { ...empty, ...value };
    }
    return value;
  } catch (error) {
    // Only a not-yet-deployed endpoint becomes the empty layout, on Custom
    // Agents pages too. Server errors, timeouts and network failures throw so
    // the page shows its error state instead of zeros.
    if (!mayUseEmpty || !isUnavailableReadError(error)) throw error;
    return emptyValue();
  }
}

/**
 * Merge the active scope into an outgoing parameter object.
 * An explicit `surface` on the call always wins, so a page can still ask a
 * narrower question inside an already-scoped mount.
 */
export function withSurface(params = {}) {
  if (!current) return params;
  if (params && params.surface != null && params.surface !== '') return params;
  return { ...params, surface: current };
}

/**
 * withSurface for an Agent Atlas read: the pinned surface by its Atlas id
 * (getAtlasSurfaceScope). Atlas's Adoption and Policy reads match a surface id exactly, so
 * `gemini_enterprise` or `otel` there is a 400, not a narrower answer.
 */
export function withAtlasSurface(params = {}) {
  const atlas = getAtlasSurfaceScope();
  if (!atlas) return params;
  if (params && params.surface != null && params.surface !== '') return params;
  return { ...params, surface: atlas };
}

/**
 * `path` with `surface=<scope>` in its query string when a scope is active —
 * for EVERY request a pinned page makes, reads and writes alike (a POST keeps
 * its body as is; the query param is added on top).
 *
 * The security service checks the org's licence per pinned surface from this
 * parameter (rbac `<from_query:surface>`); a pinned call without it reads as an
 * aggregate request and is not licence-checked, so each transport below routes
 * its paths through here rather than relying on every call site to remember.
 * A path that already names a surface keeps its own (an explicit narrower
 * question wins, as in withSurface).
 */
export function withSurfaceQuery(path) {
  if (!current || typeof path !== 'string' || !path) return path;
  const hashAt = path.indexOf('#');
  const hash = hashAt >= 0 ? path.slice(hashAt) : '';
  const bare = hashAt >= 0 ? path.slice(0, hashAt) : path;
  if (/[?&]surface=/.test(bare)) return path;
  const sep = bare.includes('?') ? (bare.endsWith('?') || bare.endsWith('&') ? '' : '&') : '?';
  return `${bare}${sep}surface=${encodeURIComponent(current)}${hash}`;
}

const surfaceScope = {
  setSurfaceScope,
  clearSurfaceScope,
  getSurfaceScope,
  getAtlasSurfaceScope,
  shouldUseEmptyResponses,
  isCustomAgentsRoute,
  isUnavailableReadError,
  readOrEmpty,
  withSurface,
  withAtlasSurface,
  withSurfaceQuery,
};
export default surfaceScope;
